/**
 * The rows behind one Summary cell. Opens when a pivot amount is clicked and
 * shows them in the review grid's own columns (same column defs, same staged
 * edits — coding a row here is the same save as on the Review tab), so the
 * modal's Totals footer adds up to the number that was clicked.
 *
 * Hosts its own ProDetailSlideOver: the page's drawer is portaled at page mount,
 * so a modal portaled later would paint over it. Mounted here, the drawer's
 * portal lands after the modal's and the same z-index puts it on top
 * (UserHistoryModal's arrangement).
 */
import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { ColumnDef } from '@tanstack/react-table';
import type { ComponentProps } from 'react';
import { TanTable } from '../../../components/TanTable';
import ModalHeader from '../../../components/ui/ModalHeader';
import ProDetailSlideOver from '../../../components/pro/ProDetailSlideOver';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import type { MatrixCellRef } from './MatrixTable';
import type { UnbilledChargeRow } from './types';

export const DRILLDOWN_SETTINGS_KEY = 'unbilled-charges-drilldown';

/**
 * Compact name for the modal's Excel export: axis, department, category
 * joined with "-" (each becomes "_" in the file name), department names
 * abbreviated so the category survives Excel's 31-character sheet name —
 * "HOUSTON-TRANS CS-Waiting Time" rather than "HOUSTON · TRANS CUSTOMER
 * SERVIC". The on-screen title keeps the full wording.
 */
export const exportNameForCell = (cell: MatrixCellRef): string =>
  [cell.axisLabel, cell.subLabel, cell.categoryLabel ?? 'All']
    .filter((part): part is string => !!part)
    .map((part) => part.replace(/customer service/gi, 'CS'))
    .join('-');

export interface DrilldownModalProps {
  readOnly?: boolean;
  settingsKey?: string;
  cell: MatrixCellRef;
  /** The window the cell was computed under, e.g. "Delivered 09/01/2026 – 09/23/2026"
   *  (plus any Summary filter that narrowed it). */
  context: string;
  rows: UnbilledChargeRow[];
  loading: boolean;
  error: string | null;
  columns: ColumnDef<UnbilledChargeRow>[];
  availableFields: ComponentProps<typeof TanTable>['availableFields'];
  additionalFields: string[];
  onAdditionalFieldsChange: (fields: string[]) => void;
  getRowId: (row: UnbilledChargeRow) => string;
  onBulkEdit: (columnId: string, rows: UnbilledChargeRow[], value: unknown) => void;
  /** Click-to-mark rows (the page owns the marker state, shared with the grid). */
  onRowClick?: (row: UnbilledChargeRow, event?: React.MouseEvent<HTMLElement>) => void;
  getRowClassName?: (row: UnbilledChargeRow, meta: { zebra: boolean }) => string;
  /** Marks cells with unsaved edits (the page owns the edit store). */
  getCellClassName?: (row: UnbilledChargeRow, columnId: string) => string;
  /** The page's "Clear highlighting" button (null while nothing is highlighted). */
  clearMarksButton?: React.ReactNode;
  /** Staged edits across the page (the modal shares the page's edit store). */
  pendingCount: number;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  /** PRO drawer state — owned by the page, rendered here while the modal is open. */
  selectedPro: number | null;
  isProDrawerOpen: boolean;
  onCloseProDrawer: () => void;
  onClose: () => void;
}

export const DrilldownModal: React.FC<DrilldownModalProps> = ({
  readOnly = false, settingsKey = DRILLDOWN_SETTINGS_KEY, cell, context, rows, loading, error, columns, availableFields, additionalFields, onAdditionalFieldsChange, getRowId, onBulkEdit,
  onRowClick, getRowClassName, getCellClassName, clearMarksButton,
  pendingCount, saving, onSave, onDiscard,
  selectedPro, isProDrawerOpen, onCloseProDrawer, onClose,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Alt+S saves the staged edits (a write, like the page's Save); Alt+C closes.
  useAltHotkeys(
    { S: () => { if (pendingCount > 0 && !saving) onSave(); }, C: onClose },
    rootRef,
    { layer: true },
  );
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  const where = cell.subLabel ? `${cell.axisLabel} · ${cell.subLabel}` : cell.axisLabel;
  const what = cell.categoryLabel ?? 'All categories';
  const title = `${where} — ${what}`;

  return createPortal(
    // z-[100] matches ProDetailSlideOver (see the header comment): the drawer
    // mounted below lands later in the DOM and so paints on top.
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Unbilled charges: ${title}`}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-scrim/50 p-4"
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl w-[95vw] h-[90vh] flex flex-col overflow-hidden"
      >
        <ModalHeader
          title={
            <span className="flex items-baseline gap-3 min-w-0">
              <span>Details</span>
              <span className="text-sm font-normal text-cmp-modal-header-muted truncate">{context}</span>
            </span>
          }
          onClose={onClose}
          dragHandleProps={dragHandleProps}
          closeLabel="Close drill-down"
        />

        <div className="flex-1 min-h-0 p-3 flex flex-col">
          {error ? (
            <div className="flex-1 flex items-center justify-center text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          ) : (
            <TanTable
              data={rows}
              columns={columns}
              availableFields={availableFields}
              additionalFields={additionalFields}
              onAdditionalFieldsChange={onAdditionalFieldsChange}
              loading={loading}
              settingsKey={settingsKey}
              seedLayoutFromCache
              // The table's own bar (which TanTable always draws — the toolbar
              // lives in it) names the cell, with its live count; the modal
              // header just says "Details" and the window.
              cardTitle={title}
              exportName={exportNameForCell(cell)}
              trackViewedColumnId="pronumber"
              defaultSorting={[{ id: 'delivery_date', desc: true }]}
              enableColumnFilters={true}
              enableGrouping={true}
              enableExcelExport={true}
              getRowId={getRowId}
              onBulkEdit={readOnly ? undefined : onBulkEdit}
              onRowClick={onRowClick}
              getRowClassName={getRowClassName}
              getCellClassName={getCellClassName}
              showColumnFooter
              fillContainer={true}
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <span className="text-xs text-muted">
            {readOnly ? 'Charges behind the selected amount.' : 'Edits here use the same reason, approval, and manager comment fields as the Review tab.'}
          </span>
          <div className="flex items-center gap-2">
            {clearMarksButton}
            {pendingCount > 0 && (
              <button
                type="button"
                onClick={onDiscard}
                disabled={saving}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Discard
              </button>
            )}
            {!readOnly && <button
              type="button"
              onClick={onSave}
              disabled={pendingCount === 0 || saving}
              className="px-3 py-1 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : pendingCount > 0 ? `Save ${pendingCount} row${pendingCount === 1 ? '' : 's'}` : 'Save'}
            </button>}
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-slate-300 rounded hover:bg-slate-50 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Same z as the modal; portaled after it, so it paints on top. */}
      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isProDrawerOpen && selectedPro !== null}
        onClose={onCloseProDrawer}
      />
    </div>,
    document.body,
  );
};

export default DrilldownModal;
